import type { ReactNode } from "react";
import { Card, Empty, ErrorBox, Spinner } from "./ui.js";

/**
 * The part of a query result this cares about.
 *
 * Deliberately not `UseQueryResult<T>`: tRPC's error type is not `Error`, so
 * the full type only matches when the error parameter is spelled out at every
 * call site. Three fields is all this needs.
 */
export interface QueryLike<T> {
  data: T | undefined;
  isLoading: boolean;
  error: unknown;
}

/**
 * Loading, error and empty handling for a query, in one place.
 *
 * Fourteen pages each retyped this triple and none of them quite agreed: some
 * returned early, some rendered the spinner and the error side by side, some
 * wrapped the error in a card and some didn't, and a couple showed a spinner
 * over data already in cache.
 */
export function Loadable<T>({
  query,
  children,
  empty,
  isEmpty,
  card = false,
}: {
  query: QueryLike<T>;
  children: (data: T) => ReactNode;
  /** shown instead of children when the data arrived but holds nothing */
  empty?: ReactNode;
  isEmpty?: (data: T) => boolean;
  /** wrap the error and empty states in a Card, for full-page use */
  card?: boolean;
}) {
  const wrap = (node: ReactNode) => (card ? <Card>{node}</Card> : node);

  // Data first: a background refetch should not blank out what's on screen.
  if (query.data !== undefined) {
    if (empty && isEmpty?.(query.data)) return <>{wrap(<Empty>{empty}</Empty>)}</>;
    return <>{children(query.data)}</>;
  }
  if (query.isLoading) return <Spinner />;
  if (query.error) {
    return <>{wrap(<div className="p-4"><ErrorBox error={query.error} /></div>)}</>;
  }
  return null;
}
